import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../api/client';
import {
  confirmNotificationVerification,
  disableNotificationDestination,
  getNotificationDestination,
  startNotificationVerification,
} from '../api/endpoints/notificationDestination';
import { createIdempotencyKeyHolder } from '../api/idempotency';
import type {
  NotificationDestinationResponse,
  NotificationVerificationStartResponse,
} from '../api/schemas';
import { usePermissions } from '../auth/usePermissions';
import { useSession } from '../auth/useSession';
import { formatDateTime } from '../lib/format';
import { ConfirmAction } from './ConfirmAction';
import { ErrorBanner } from './ErrorBanner';
import { LoadingState } from './LoadingState';

const QUERY_KEY = 'notification-destination';
type VerificationChallenge = NotificationVerificationStartResponse['data'];

/** Owner-only controls for the WhatsApp number that receives operational alerts. */
export function NotificationDestinationCard() {
  const { session } = useSession();
  const owner = usePermissions().can('negocio');
  const businessId = session?.businessId ?? '';
  const queryClient = useQueryClient();
  const queryKey = [QUERY_KEY, businessId] as const;

  const startKey = useRef(createIdempotencyKeyHolder()).current;
  const confirmKey = useRef(createIdempotencyKeyHolder()).current;
  const disableKey = useRef(createIdempotencyKeyHolder()).current;

  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [challenge, setChallenge] = useState<VerificationChallenge>();
  const [changingNumber, setChangingNumber] = useState(false);
  const [notice, setNotice] = useState<string>();

  const destinationQuery = useQuery({
    queryKey,
    queryFn: getNotificationDestination,
    enabled: owner && Boolean(businessId),
    retry: false,
  });

  const startMutation = useMutation({
    mutationFn: (nextPhone: string) =>
      startNotificationVerification(
        { channel: 'whatsapp', phone: nextPhone },
        startKey.ensure(),
      ),
    onSuccess: ({ data }) => {
      setChallenge(data);
      setCode('');
      setNotice(undefined);
    },
    onSettled: () => startKey.reset(),
  });

  const confirmMutation = useMutation({
    mutationFn: () => {
      if (!challenge) throw new Error('Missing verification challenge.');
      return confirmNotificationVerification(
        { challengeId: challenge.challengeId, code },
        confirmKey.ensure(),
      );
    },
    onSuccess: (response) => {
      queryClient.setQueryData<NotificationDestinationResponse>(queryKey, response);
      setChallenge(undefined);
      setPhone('');
      setCode('');
      setChangingNumber(false);
      setNotice('Listo. Ese WhatsApp quedó confirmado para los avisos operativos.');
    },
    onSettled: () => confirmKey.reset(),
  });

  const disableMutation = useMutation({
    mutationFn: () => disableNotificationDestination(disableKey.ensure()),
    onSuccess: (response) => {
      queryClient.setQueryData<NotificationDestinationResponse>(queryKey, response);
      setChallenge(undefined);
      setChangingNumber(false);
      setNotice('Los avisos por WhatsApp quedaron desactivados.');
    },
    onSettled: () => disableKey.reset(),
  });

  const resendSeconds = useResendSeconds(challenge?.resendAfter);
  const destination = destinationQuery.data?.data ?? null;
  const showSetup = destination?.status !== 'enabled' || changingNumber;

  if (!owner) return null;

  return (
    <section className="section" aria-labelledby="notification-destination-title">
      <h2 className="section-label" id="notification-destination-title">
        Avisos operativos
      </h2>
      <div className="card notification-destination-card">
        {destinationQuery.isLoading ? <LoadingState label="Cargando avisos…" /> : null}
        {destinationQuery.isError ? (
          <ErrorBanner message={notificationDestinationErrorMessage(destinationQuery.error)} />
        ) : null}

        {!destinationQuery.isLoading && !destinationQuery.isError ? (
          <>
            <div className="row notification-destination-summary">
              <div className="grow">
                <p
                  className="status"
                  data-tone={destination?.status === 'enabled' ? 'good' : 'off'}
                >
                  {destination?.status === 'enabled'
                    ? 'Número listo'
                    : destination
                      ? 'Desactivado'
                      : 'Sin configurar'}
                </p>
                <p className="row-title">
                  {destination ? `WhatsApp ${destination.maskedDestination}` : 'WhatsApp del negocio'}
                </p>
                <p className="row-sub">
                  {destination?.status === 'enabled'
                    ? `Confirmado el ${formatDateTime(destination.verifiedAt)}. Quedó listo para los avisos operativos cuando Dale los habilite.`
                    : destination
                      ? 'Este número fue confirmado, pero ahora no recibe avisos.'
                      : 'Confirma un número para dejarlo listo para los avisos operativos.'}
                </p>
              </div>
              {destination?.status === 'enabled' ? (
                <button
                  type="button"
                  className="btn small secondary"
                  onClick={() => {
                    setChangingNumber((current) => !current);
                    setChallenge(undefined);
                    setCode('');
                    setNotice(undefined);
                  }}
                >
                  {changingNumber ? 'Cancelar cambio' : 'Cambiar número'}
                </button>
              ) : null}
            </div>

            {notice ? (
              <p className="notice" role="status" aria-live="polite">
                {notice}
              </p>
            ) : null}

            {showSetup ? (
              <VerificationForm
                phone={phone}
                code={code}
                challenge={challenge}
                resendSeconds={resendSeconds}
                startPending={startMutation.isPending}
                confirmPending={confirmMutation.isPending}
                startError={startMutation.error}
                confirmError={confirmMutation.error}
                onPhoneChange={setPhone}
                onCodeChange={setCode}
                onStart={() => startMutation.mutate(phone)}
                onConfirm={() => confirmMutation.mutate()}
              />
            ) : null}

            {destination?.status === 'enabled' ? (
              <div className="notification-destination-disable">
                {disableMutation.isError ? (
                  <ErrorBanner message={notificationDestinationErrorMessage(disableMutation.error)} />
                ) : null}
                <ConfirmAction
                  label="Desactivar número"
                  confirmLabel="Sí, desactivar"
                  pendingLabel="Desactivando…"
                  variant="danger"
                  pending={disableMutation.isPending}
                  onConfirm={() => disableMutation.mutate()}
                />
                <p className="row-sub">
                  Las reservas seguirán funcionando. Dale dejará de usar este número para avisos operativos.
                </p>
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    </section>
  );
}

type VerificationFormProps = {
  phone: string;
  code: string;
  challenge: VerificationChallenge | undefined;
  resendSeconds: number;
  startPending: boolean;
  confirmPending: boolean;
  startError: unknown;
  confirmError: unknown;
  onPhoneChange: (value: string) => void;
  onCodeChange: (value: string) => void;
  onStart: () => void;
  onConfirm: () => void;
};

function VerificationForm({
  phone,
  code,
  challenge,
  resendSeconds,
  startPending,
  confirmPending,
  startError,
  confirmError,
  onPhoneChange,
  onCodeChange,
  onStart,
  onConfirm,
}: VerificationFormProps) {
  const submitPhone = (event: FormEvent) => {
    event.preventDefault();
    onStart();
  };
  const submitCode = (event: FormEvent) => {
    event.preventDefault();
    onConfirm();
  };

  return (
    <div className="notification-verification">
      {!challenge ? (
        <form className="form-grid" onSubmit={submitPhone}>
          <div className="field">
            <label className="field-label" htmlFor="notification-phone">
              Número de WhatsApp
            </label>
            <input
              id="notification-phone"
              type="tel"
              autoComplete="tel"
              inputMode="tel"
              value={phone}
              onChange={(event) => onPhoneChange(event.target.value)}
              placeholder="+507 6000-0000"
              aria-describedby="notification-phone-hint"
              required
            />
            <p className="field-help" id="notification-phone-hint">
              Usa el número que atiende las reservas del negocio.
            </p>
          </div>
          {startError ? (
            <ErrorBanner message={notificationDestinationErrorMessage(startError)} />
          ) : null}
          <button type="submit" className="btn small" disabled={startPending}>
            {startPending ? 'Enviando…' : 'Enviar código'}
          </button>
        </form>
      ) : (
        <>
          <div className="notification-challenge" role="status" aria-live="polite">
            <p className="status" data-tone="hold">
              Pendiente
            </p>
            <p className="row-title">Código enviado a {challenge.maskedDestination}</p>
            <p className="row-sub">
              Todavía no confirmamos este número. El código vence el {formatDateTime(challenge.expiresAt)}.
            </p>
          </div>
          <form className="form-grid" onSubmit={submitCode}>
            <div className="field">
              <label className="field-label" htmlFor="notification-code">
                Código de 6 dígitos
              </label>
              <input
                id="notification-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6}"
                maxLength={6}
                value={code}
                onChange={(event) => onCodeChange(event.target.value.replace(/\D/g, ''))}
                aria-describedby="notification-code-hint"
                required
              />
              <p className="field-help" id="notification-code-hint">
                Confírmalo para empezar a recibir avisos.
              </p>
            </div>
            {confirmError ? (
              <ErrorBanner message={notificationDestinationErrorMessage(confirmError)} />
            ) : null}
            {startError ? (
              <ErrorBanner message={notificationDestinationErrorMessage(startError)} />
            ) : null}
            <span className="action-pair">
              <button type="submit" className="btn small" disabled={confirmPending || code.length !== 6}>
                {confirmPending ? 'Confirmando…' : 'Confirmar número'}
              </button>
              <button
                type="button"
                className="btn small ghost"
                disabled={startPending || resendSeconds > 0}
                onClick={onStart}
              >
                {startPending
                  ? 'Enviando…'
                  : resendSeconds > 0
                    ? `Reenviar en ${resendSeconds}s`
                    : 'Reenviar código'}
              </button>
            </span>
          </form>
        </>
      )}
    </div>
  );
}

function useResendSeconds(resendAfter: string | undefined): number {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    setNow(Date.now());
    if (!resendAfter || Date.parse(resendAfter) <= Date.now()) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, [resendAfter]);

  if (!resendAfter) return 0;
  const resendAt = Date.parse(resendAfter);
  if (Number.isNaN(resendAt)) return 0;
  return Math.max(0, Math.ceil((resendAt - now) / 1_000));
}

export function notificationDestinationErrorMessage(error: unknown): string {
  if (!(error instanceof ApiError)) return 'No pudimos completar eso ahora mismo.';
  if (error.status === 401) return 'Tu sesión venció. Vuelve a entrar.';
  if (error.status === 403) return 'Solo el dueño puede cambiar este número.';
  if (error.status === 409 && error.code === 'verification_invalid') {
    return 'Ese código no coincide. Revísalo y vuelve a intentar.';
  }
  if (error.status === 409) return 'Ese intento ya no está vigente. Pide un código nuevo.';
  if (error.status === 410) return 'Ese código venció. Pide uno nuevo.';
  if (error.status === 429 && error.code === 'verification_locked') {
    return 'Se agotaron los intentos. Pide un código nuevo en unos minutos.';
  }
  if (error.status === 429) return 'Demasiados intentos. Espera un momento.';
  if (error.status === 503) return 'La verificación por WhatsApp no está disponible todavía.';
  return error.message;
}
